import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { ParsedDocument } from '../../lib/brdocs';
import { Scanner } from '../scan/scanner';
import { canvasToPixels } from '../scan/browser-render';
import { playConfirmationBeep } from '../shared/beep';
import { CaptureDebugEvent } from '../shared/debug-event';
import { CameraStreamService } from '../docpic/camera-stream.service';
import { focusAtPoint, tapToNormalizedPoint } from '../docpic/camera-focus';

/** Minimum gap between decode attempts — throttles to ~7 scans/sec. */
const SCAN_INTERVAL_MS = 140;
/** JPEG quality for the still kept when the QR decodes. */
const STILL_QUALITY = 0.85;

/** Emitted when a document's QR/MRZ is successfully decoded. */
export interface QrCaptureResult {
  /** The parsed document (fields, embedded photo, signature). */
  doc: ParsedDocument;
  /** The video still at the moment of the decode, as a JPEG data URL. */
  still: string;
}

/**
 * A focused, kiosk-style **live QR reader**. It opens the camera and decodes
 * every frame through the brdocs {@link Scanner} until a document's QR/MRZ comes
 * through, then beeps, grabs the matching still, and emits {@link QrCaptureResult}.
 *
 * Unlike re-reading a QR out of a document photo, this asks the user to bring
 * the code close to the camera — far more reliable for the dense CNH-e / CIN
 * codes. The host (the guided flow) shows the instructions and framing overlay.
 */
@Component({
  selector: 'app-qr-capture',
  templateUrl: './qr-capture.html',
  styleUrls: ['./qr-capture.scss'],
})
export class QrCapture implements AfterViewInit, OnDestroy {
  @ViewChild('video') private video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') private canvas!: ElementRef<HTMLCanvasElement>;

  private stream?: MediaStream;
  private rafId?: number;
  private busy = false;
  private lastScanAt = 0;
  private done = false;
  protected focusRingPoint: { xPct: number; yPct: number } | null = null;
  private focusRingTimer?: ReturnType<typeof setTimeout>;

  /** Fires once when a QR/MRZ is decoded. */
  @Output() decoded = new EventEmitter<QrCaptureResult>();
  /** Fires if the camera can't be opened, so the host can offer a way out. */
  @Output() cameraError = new EventEmitter<string>();
  /** Low-level diagnostics (camera + QR decode). */
  @Output() debug = new EventEmitter<CaptureDebugEvent>();

  constructor(
    private scanner: Scanner,
    private cdr: ChangeDetectorRef,
    @Optional() private camera?: CameraStreamService,
  ) {}

  async ngAfterViewInit(): Promise<void> {
    try {
      // Reuse the flow's shared stream when present (no reopen / re-permission);
      // otherwise open a direct high-res back-camera stream.
      this.stream = this.camera
        ? await this.camera.acquire({ facingMode: 'environment' })
        : await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false,
          });
      const video = this.video.nativeElement;
      video.srcObject = this.stream;
      await video.play();
      this.debug.emit({ level: 'info', category: 'camera', message: 'QR camera started' });
      this.rafId = requestAnimationFrame(this.tick);
    } catch (e) {
      this.debug.emit({
        level: 'error',
        category: 'camera',
        message: 'QR camera failed to open',
        data: { error: String(e) },
      });
      this.cameraError.emit(String(e));
    }
  }

  /** Throttled decode loop: grab a frame, scan it, emit on the first hit. */
  private tick = (): void => {
    if (this.done) return;
    const now = performance.now();
    const video = this.video.nativeElement;
    if (
      !this.busy &&
      now - this.lastScanAt >= SCAN_INTERVAL_MS &&
      video.readyState === video.HAVE_ENOUGH_DATA
    ) {
      this.lastScanAt = now;
      const canvas = this.canvas.nativeElement;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        this.busy = true;
        this.scanner
          .scanPixels(canvasToPixels(canvas))
          .then((res) => {
            if (res.document && !this.done) {
              this.done = true;
              const still = canvas.toDataURL('image/jpeg', STILL_QUALITY);
              this.debug.emit({
                level: 'info',
                category: 'qr',
                message: `QR decoded: ${res.document.kind}`,
                data: { kind: res.document.kind, signature: res.document.signature?.status },
              });
              playConfirmationBeep();
              this.decoded.emit({ doc: res.document, still });
            }
          })
          .catch(() => {})
          .finally(() => {
            this.busy = false;
          });
      }
    }
    if (!this.done) this.rafId = requestAnimationFrame(this.tick);
  };

  /** Tap-to-focus for the QR reader (Tier 1 continuous AF comes from the service). */
  protected onPreviewTap(ev: MouseEvent): void {
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const p = tapToNormalizedPoint(rect, ev.clientX, ev.clientY);
    this.focusRingPoint = { xPct: p.x * 100, yPct: p.y * 100 };
    if (this.focusRingTimer) clearTimeout(this.focusRingTimer);
    this.focusRingTimer = setTimeout(() => {
      this.focusRingPoint = null;
      try { this.cdr.detectChanges(); } catch {}
    }, 700);
    const track = this.camera?.videoTrack() ?? this.stream?.getVideoTracks?.()[0];
    if (track) void focusAtPoint(track, p.x, p.y);
    try { this.cdr.detectChanges(); } catch {}
  }

  ngOnDestroy(): void {
    this.done = true;
    if (this.rafId !== undefined) cancelAnimationFrame(this.rafId);
    if (this.focusRingTimer) clearTimeout(this.focusRingTimer);
    if (this.camera) {
      // Shared stream — service owns teardown; just detach from our <video>.
      if (this.video?.nativeElement) this.video.nativeElement.srcObject = null;
    } else {
      this.stream?.getTracks().forEach((t) => t.stop());
    }
    this.stream = undefined;
  }
}
